import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../api/client";
import { keys, useInvalidate, useMe } from "../api/hooks";
import { enabledResponseSchema, pausedResponseSchema, type LocationSummary } from "../../api/contracts";
import { useShellPrefix } from "../lib/shell";
import { trackAction } from "../lib/usage-track";
import { Button, Dialog, EmptyState, ErrorNote, Panel, Toggle } from "./ui";
import { DataTable, type Column } from "./DataTable";
import { StatusDot } from "./StatusDot";
import { Timestamp } from "./Timestamp";
import { EnableDialog } from "./EnableDialog";

const SCOPE: Record<LocationSummary["ruleScope"], string> = { global: "Corporate", owner: "Owner", location: "Location" };

function LocationStatus({ location: l }: { location: LocationSummary }) {
  if (l.windingDown) return <StatusDot tone="warning" label="Removing" detail={`${l.activeBlocks} left`} />;
  if (l.enabled && l.paused) return <StatusDot tone="warning" label="Paused" />;
  return l.enabled ? <StatusDot tone="success" label="On" /> : <StatusDot tone="neutral" label="Off" />;
}

/** What the switch is doing right now, next to it, so "on" is never just a colour. */
export function switchCaption(l: LocationSummary, live: boolean): string {
  if (!l.enabled) return "Not placing blocks";
  if (l.paused) return "Paused, no changes";
  return live ? "Placing blocks" : "Planning only";
}

/** The outlined-pill look of a secondary Button, on a link. */
const PILL_LINK = "inline-flex h-[38px] items-center justify-center whitespace-nowrap rounded-full border border-hs-primary bg-hs-surface px-4 text-[13px] font-semibold text-hs-primary hover:bg-hs-primary-soft/60";

/**
 * Locations as rows, for owners (a handful, roomy) and admins (the fleet, compact and selectable).
 * Owns the on/off dialogs and the pause mutation, as LocationGrid did. Below `sm` the rows stack.
 */
export function LocationTable({ locations, showOwner, selected, onSelectedChange, emptyTitle, emptyBody, disabled, density = "default" }: {
  locations: LocationSummary[]; showOwner?: boolean; selected?: Set<string>; onSelectedChange?: (next: Set<string>) => void;
  emptyTitle: string; emptyBody: string; disabled?: boolean; density?: "default" | "compact";
}) {
  const meQuery = useMe();
  const prefix = useShellPrefix();
  const invalidate = useInvalidate();
  const [enabling, setEnabling] = useState<LocationSummary | null>(null);
  const [disabling, setDisabling] = useState<LocationSummary | null>(null);
  const setEnabled = useMutation({
    mutationFn: ({ blvdId, enabled }: { blvdId: string; enabled: boolean }) => api(`/api/locations/${blvdId}/enabled`, enabledResponseSchema, { method: "POST", body: { enabled } }),
    onSuccess: (_data, { blvdId, enabled }) => { setEnabling(null); setDisabling(null); void invalidate(keys.locations); trackAction("location_toggled", { blvdId, enabled }); },
  });
  const setPaused = useMutation({
    mutationFn: ({ blvdId, paused }: { blvdId: string; paused: boolean }) => api(`/api/locations/${blvdId}/paused`, pausedResponseSchema, { method: "POST", body: { paused } }),
    onSuccess: () => void invalidate(keys.locations),
  });
  const busy = setEnabled.isPending || setPaused.isPending || Boolean(disabled);

  // `me` is normally cached before this mounts (RequireAuth resolves it first); guard the brief instant it is not.
  if (!meQuery.data) return null;
  const me = meQuery.data;
  const live = me.mode === "live";

  // Stacked (below sm): line 1 is name, status and the toggle; line 2 the muted numbers and last plan; line 3 the links.
  // Numeric cells are wrapped in a span: when stacked, the cell also holds the inline label, so the cell's own text is not the number.
  const muted = "text-xs text-hs-text-muted";
  const columns: Column<LocationSummary>[] = [
    { key: "name", header: "Location", stackedClass: "min-w-0 grow", cell: (l) => (
      <div className="min-w-0">
        <p className="truncate font-semibold text-hs-text">{l.name}</p>
        <p className="truncate text-xs font-normal text-hs-text">{[l.state, l.timezone.replace(/_/g, " "), showOwner ? l.ownerIdentifier : null].filter(Boolean).join(" · ")}</p>
      </div>
    ) },
    { key: "status", header: "Status", width: "8rem", cell: (l) => <LocationStatus location={l} /> },
    { key: "placed", header: live ? "Placed" : "Would place", width: "5rem", stackedLine: 2, stackedClass: muted, stackedLabel: live ? "Placed" : "Would place",
      cell: (l) => <span>{live ? l.weekCounts.created : l.weekCounts.wouldCreate}</span> },
    { key: "removed", header: live ? "Removed" : "Would remove", width: "5.5rem", stackedLine: 2, stackedClass: muted, stackedLabel: live ? "Removed" : "Would remove",
      cell: (l) => <span>{live ? l.weekCounts.removed : l.weekCounts.wouldRemove}</span> },
    { key: "rules", header: "Rules", width: "7rem", stackedLine: 2, stackedClass: muted, stackedLabel: "Rules", cell: (l) => SCOPE[l.ruleScope] },
    { key: "lastPlan", header: "Last plan", width: "7rem", stackedLine: 2, stackedClass: muted, stackedLabel: "Last plan", cell: (l) => <Timestamp at={l.lastPlannedAt} /> },
    { key: "toggle", header: "Automatic BTB blocks", width: "11rem", stackedClass: "ml-auto",
      cell: (l) => (
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-sm font-medium text-hs-text">{switchCaption(l, live)}</span>
          <Toggle checked={l.enabled} onChange={(next) => (next ? setEnabling(l) : setDisabling(l))} label={`BTB blocks for ${l.name}`} disabled={busy} />
        </div>
      ) },
    { key: "actions", header: "Actions", align: "right", stackedLine: 3, stackedClass: "ml-auto", cell: (l) => (
      <div className="flex items-center justify-end gap-2">
        <Link to={`${prefix}/locations/${l.blvdId}/preview`} aria-label={`Preview the next ${l.lookaheadDays} days for ${l.name}`} className={PILL_LINK}>Preview</Link>
        {l.enabled && <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPaused.mutate({ blvdId: l.blvdId, paused: !l.paused })}>{l.paused ? "Resume" : "Pause"}</Button>}
      </div>
    ) },
  ];

  return (
    <>
      <ErrorNote error={setEnabled.error ?? setPaused.error} />
      {locations.length === 0
        ? <EmptyState title={emptyTitle} body={emptyBody} />
        : <Panel padding="none">
            <DataTable columns={columns} rows={locations} rowKey={(l) => l.blvdId} density={density} stickyHeader={density === "compact"} stacked caption="Locations"
              selectable={selected && onSelectedChange ? { selected, onChange: onSelectedChange, label: (l) => `Select ${l.name}` } : undefined} />
          </Panel>}
      {enabling && <EnableDialog location={enabling} mode={me.mode} busy={busy} rulesHref={`${prefix}/rules?location=${enabling.blvdId}`} previewHref={`${prefix}/locations/${enabling.blvdId}/preview`} onClose={() => setEnabling(null)} onConfirm={() => setEnabled.mutate({ blvdId: enabling.blvdId, enabled: true })} />}
      <Dialog open={disabling !== null} title={`Turn off BTB blocks for ${disabling?.name}?`} onClose={() => setDisabling(null)}
        footer={<><Button variant="ghost" onClick={() => setDisabling(null)} disabled={busy}>Cancel</Button><Button variant="danger" disabled={busy} onClick={() => disabling && setEnabled.mutate({ blvdId: disabling.blvdId, enabled: false })}>Turn off</Button></>}>
        <p>No new BTB blocks will be planned for this location. Blocks already on the calendar stay until someone removes them.</p>
        <ErrorNote error={setEnabled.error} />
      </Dialog>
    </>
  );
}
